import type { Uuid } from "@/domains/global/identifiers";
import {
  getMeetingHandoffPreviousAudioRecordingId,
  getMeetingHandoffSuccessorAudioRecordingId,
} from "@/store/audio-recordings/app-events";
import type { AudioRecordingModelData } from "@/store/audio-recordings/types";
import type {
  AudioUploadMergeIntentPayload,
  UpsertNoteSuggestionAudioRecordingMergeIntentPayload,
} from "@/store/sync/operations/types";

type MergeRecordingRelationship = {
  canonicalAudioRecordingId: Uuid;
  sourceAudioRecordingIds: Uuid[];
};

function resolveMeetingHandoffMergeRelationship({
  audioRecordingId,
  modelData,
}: {
  audioRecordingId: Uuid;
  modelData: AudioRecordingModelData;
}): MergeRecordingRelationship | null {
  const previousAudioRecordingId = getMeetingHandoffPreviousAudioRecordingId({
    modelData,
  });
  if (previousAudioRecordingId) {
    return {
      canonicalAudioRecordingId: audioRecordingId,
      sourceAudioRecordingIds: [previousAudioRecordingId, audioRecordingId],
    };
  }

  const successorAudioRecordingId = getMeetingHandoffSuccessorAudioRecordingId({
    modelData,
  });
  if (successorAudioRecordingId) {
    return {
      canonicalAudioRecordingId: successorAudioRecordingId,
      sourceAudioRecordingIds: [audioRecordingId, successorAudioRecordingId],
    };
  }

  return null;
}

/**
 * Builds upload-side merge readiness for either segment of a detected-meeting handoff.
 */
export function buildAudioUploadMergeIntentPayload({
  audioRecordingId,
  modelData,
}: {
  audioRecordingId: Uuid;
  modelData: AudioRecordingModelData;
}): AudioUploadMergeIntentPayload | undefined {
  const relationship = resolveMeetingHandoffMergeRelationship({
    audioRecordingId,
    modelData,
  });
  if (!relationship) return undefined;

  return {
    canonical_audio_recording_id: relationship.canonicalAudioRecordingId,
    source_audio_recording_ids: relationship.sourceAudioRecordingIds,
  };
}

/**
 * Builds note-side merge readiness for the successor recording that owns the suggestion.
 */
export function buildNoteSuggestionMergeIntentPayload({
  audioRecordingId,
  modelData,
}: {
  audioRecordingId: Uuid;
  modelData: AudioRecordingModelData;
}): UpsertNoteSuggestionAudioRecordingMergeIntentPayload | undefined {
  const relationship = resolveMeetingHandoffMergeRelationship({
    audioRecordingId,
    modelData,
  });
  if (!relationship || relationship.canonicalAudioRecordingId !== audioRecordingId) {
    return undefined;
  }

  return {
    canonical_audio_recording_id: relationship.canonicalAudioRecordingId,
    source_audio_recording_ids: relationship.sourceAudioRecordingIds,
  };
}
